// Assistant ZOS : bouton du bandeau + fenêtre de discussion flottante (en bas à droite).
//
// La fenêtre ne couvre pas la page : le dashboard reste visible et utilisable derrière,
// et seul le contenu de la fenêtre défile. Trois onglets : Accueil, Conversation, Aide.
//
// Les questions partent vers /api/assistant/chat, qui interroge les données de
// l'organisation connectée (lecture seule). La conversation est gardée pour la
// session de l'onglet (sessionStorage), pas au-delà.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { askAssistant, type AssistantAttachment, type AssistantMessage } from '../api/assistant';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import type { TranslationKey } from '../i18n/translations';

interface Turn extends AssistantMessage {
  attachments?: AssistantAttachment[];
  toolsUsed?: string[];
  error?: boolean;
}

const STORAGE_KEY = 'zos-assistant-conversation';
type TFn = ReturnType<typeof useI18n>['t'];
const SUGGESTION_KEYS: TranslationKey[] = [
  'adm_ai_sugg_offline',
  'adm_ai_sugg_week',
  'adm_ai_sugg_alerts',
  'adm_ai_sugg_top_device',
  'adm_ai_sugg_summary',
];
const TOOL_LABELS: Record<string, TranslationKey> = {
  lister_installations: 'adm_ai_tool_installations',
  etat_equipements: 'adm_ai_tool_devices',
  consommation: 'adm_ai_tool_consumption',
  alertes: 'adm_ai_tool_alerts',
  sessions_de_charge: 'adm_ai_tool_sessions',
};

// ─── Rendu markdown minimal (gras, code, liens, listes, tableaux) ───
function inline(text: string, onLink: (href: string) => void, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) out.push(<strong key={`${key}-${i++}`} className="font-semibold text-zce-navy">{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={`${key}-${i++}`} className="px-1 py-0.5 rounded bg-slate-100 text-[12px] font-mono">{tok.slice(1, -1)}</code>);
    else {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok)!;
      const href = lm[2];
      const internal = href.startsWith('/');
      out.push(internal
        ? <button key={`${key}-${i++}`} type="button" onClick={() => onLink(href)} className="text-zce-blue font-medium underline underline-offset-2 hover:text-blue-700">{lm[1]}</button>
        : <a key={`${key}-${i++}`} href={href} target="_blank" rel="noreferrer" className="text-zce-blue underline underline-offset-2">{lm[1]}</a>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Markdown({ text, onLink }: { text: string; onLink: (href: string) => void }) {
  const lines = text.replace(/\r/g, '').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0; let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    // Tableau
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const cells = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { rows.push(cells(lines[i])); i++; }
      blocks.push(
        <div key={k++} className="overflow-x-auto -mx-1">
          <table className="min-w-full text-[12.5px] border-collapse">
            <thead><tr>{head.map((h, j) => <th key={j} className="text-left font-semibold text-slate-500 border-b border-slate-200 px-2 py-1.5 whitespace-nowrap">{inline(h, onLink, `h${k}-${j}`)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri} className="border-b border-slate-100 last:border-0">{r.map((c, j) => <td key={j} className="px-2 py-1.5 tabular-nums align-top">{inline(c, onLink, `c${k}-${ri}-${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }
    // Listes
    const bullet = /^\s*[-*•]\s+(.*)$/; const numbered = /^\s*\d+[.)]\s+(.*)$/;
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = numbered.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? numbered : bullet).test(lines[i])) {
        items.push((ordered ? numbered : bullet).exec(lines[i])![1]); i++;
      }
      const cls = 'pl-5 space-y-1 ' + (ordered ? 'list-decimal' : 'list-disc');
      blocks.push(ordered
        ? <ol key={k++} className={cls}>{items.map((it, j) => <li key={j}>{inline(it, onLink, `l${k}-${j}`)}</li>)}</ol>
        : <ul key={k++} className={cls}>{items.map((it, j) => <li key={j}>{inline(it, onLink, `l${k}-${j}`)}</li>)}</ul>);
      continue;
    }
    // Paragraphe (les titres markdown éventuels sont rendus en gras)
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !bullet.test(lines[i]) && !numbered.test(lines[i]) && !/^\s*\|.*\|\s*$/.test(lines[i])) {
      para.push(lines[i].replace(/^#{1,6}\s+(.*)$/, '**$1**')); i++;
    }
    blocks.push(<p key={k++}>{para.map((p, j) => <Fragment key={j}>{j > 0 && <br />}{inline(p, onLink, `p${k}-${j}`)}</Fragment>)}</p>);
  }
  return <div className="space-y-2.5">{blocks}</div>;
}

// ─── Mini-histogramme pour les séries jointes ───
function SeriesChart({ a }: { a: AssistantAttachment }) {
  const { t, locale } = useI18n();
  const pts = a.points.slice(-31);
  const max = Math.max(...pts.map(p => p.value), 0) || 1;
  const total = pts.reduce((s, p) => s + p.value, 0);
  const W = 320, H = 96, pad = 4, bw = (W - pad * 2) / pts.length;
  const labelEvery = Math.ceil(pts.length / 7);
  const fmt = (v: number) => v.toLocaleString(locale, { maximumFractionDigits: v < 10 ? 2 : 1 });
  return (
    <figure className="mt-2 rounded-xl border border-slate-200 bg-white p-3">
      <figcaption className="flex items-baseline justify-between gap-2 text-[11.5px]">
        <span className="font-semibold text-slate-600 truncate">{a.title}</span>
        <span className="text-slate-500 tabular-nums whitespace-nowrap">{t('adm_ai_total', { value: fmt(total), unit: a.unit })}</span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H + 16}`} className="w-full h-auto mt-2" role="img" aria-label={a.title}>
        <line x1={pad} x2={W - pad} y1={H} y2={H} stroke="#e2e8f0" />
        {pts.map((p, i) => {
          const h = Math.max(1.5, (p.value / max) * (H - 8));
          const x = pad + i * bw;
          return (
            <g key={i}>
              <rect x={x + bw * 0.15} y={H - h} width={bw * 0.7} height={h} rx={2} fill={p.value === max ? '#0d3b66' : '#1e88e5'} opacity={p.value === max ? 1 : 0.8}>
                <title>{t('adm_ai_point', { label: p.label, value: fmt(p.value), unit: a.unit })}</title>
              </rect>
              {i % labelEvery === 0 && <text x={x + bw / 2} y={H + 12} textAnchor="middle" fontSize="9" fill="#64748b">{p.label}</text>}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

// ─── Emblème : le « bâtiment-robot » ───
// Un immeuble dont les fenêtres sont des yeux, une antenne sur le toit (le signal des
// capteurs) et une porte : le bâtiment intelligent qui répond. Dessiné en SVG, il reste net
// à toutes les tailles, sans image à charger.
let emblemSeq = 0;
function AssistantAvatar({ size = 40, glow = false }: { size?: number; glow?: boolean }) {
  const [id] = useState(() => `zos-bot-${++emblemSeq}`);
  return (
    <span className="relative inline-flex flex-none" style={{ width: size, height: size }}>
      {glow && <span className="absolute -inset-1.5 rounded-[30%] bg-gradient-to-br from-cyan-300/60 via-sky-400/40 to-emerald-300/60 blur-md zce-pulse-dot" aria-hidden="true" />}
      <svg className="relative w-full h-full drop-shadow-md" viewBox="0 0 48 48" aria-hidden="true">
        <defs>
          <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#22d3ee" />
            <stop offset=".55" stopColor="#1e88e5" />
            <stop offset="1" stopColor="#0d3b66" />
          </linearGradient>
          <linearGradient id={`${id}-bd`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#dbeafe" />
          </linearGradient>
        </defs>
        {/* Pastille */}
        <rect x="1" y="1" width="46" height="46" rx="14" fill={`url(#${id}-bg)`} />
        <rect x="1.5" y="1.5" width="45" height="45" rx="13.5" fill="none" stroke="#ffffff" strokeOpacity=".35" />
        {/* Antenne + signal */}
        <path d="M24 12.5V8.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
        <circle cx="24" cy="7" r="2.4" fill="#34d399" stroke="#ffffff" strokeWidth="1.2" />
        <path d="M19.5 6.2a6.5 6.5 0 0 1 9 0" fill="none" stroke="#a7f3d0" strokeWidth="1.3" strokeLinecap="round" opacity=".9" />
        {/* Oreilles / ailes du bâtiment */}
        <rect x="9.5" y="21" width="4.5" height="10" rx="2" fill={`url(#${id}-bd)`} opacity=".9" />
        <rect x="34" y="21" width="4.5" height="10" rx="2" fill={`url(#${id}-bd)`} opacity=".9" />
        {/* Corps : l'immeuble */}
        <rect x="13" y="12.5" width="22" height="28" rx="5" fill={`url(#${id}-bd)`} />
        {/* Yeux = fenêtres éclairées */}
        <rect x="16.8" y="18.5" width="5.6" height="6.4" rx="2" fill="#0d3b66" />
        <rect x="25.6" y="18.5" width="5.6" height="6.4" rx="2" fill="#0d3b66" />
        <circle cx="19.6" cy="21.7" r="1.5" fill="#22d3ee" />
        <circle cx="28.4" cy="21.7" r="1.5" fill="#22d3ee" />
        {/* Sourire = rangée de fenêtres */}
        <path d="M18.5 29.2c3.4 2.3 7.6 2.3 11 0" fill="none" stroke="#1e88e5" strokeWidth="2" strokeLinecap="round" />
        {/* Porte */}
        <rect x="21.2" y="33.5" width="5.6" height="7" rx="1.6" fill="#0d3b66" />
        <circle cx="25.4" cy="37.2" r=".7" fill="#34d399" />
      </svg>
    </span>
  );
}

// ─── Icônes de la barre d'onglets ───
const TabIcon = {
  home: <path d="M3 11 12 3l9 8M5 10v10h14V10M10 20v-6h4v6" />,
  chat: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.3A8 8 0 1 1 21 12z" />,
  help: <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2V5zM4 21a2 2 0 0 1 2-2h13" />,
};
type Tab = keyof typeof TabIcon;

const getHelpSections = (t: TFn) => [
  { t: t('adm_ai_help_can_title'), b: [t('adm_ai_help_can_1'), t('adm_ai_help_can_2'), t('adm_ai_help_can_3'), t('adm_ai_help_can_4'), t('adm_ai_help_can_5')] },
  { t: t('adm_ai_help_cannot_title'), b: [t('adm_ai_help_cannot_1'), t('adm_ai_help_cannot_2')] },
  { t: t('adm_ai_help_privacy_title'), b: [t('adm_ai_help_privacy_1'), t('adm_ai_help_privacy_2'), t('adm_ai_help_privacy_3')] },
];

// ─── Composant principal ───
export function AssistantPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // `tr` (et non `t`) : `t` désigne un tour de conversation dans ce composant.
  const { t: tr } = useI18n();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });
  const [tab, setTab] = useState<Tab>(() => (turns.length ? 'chat' : 'home'));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const homeInputRef = useRef<HTMLTextAreaElement>(null);

  // ── Défilement de la conversation ──
  // Réponse reçue : on affiche son DÉBUT (elle se lit de haut en bas). Si l'utilisateur est
  // remonté relire un ancien message, on ne bouge pas : un bouton « Nouvelle réponse » apparaît.
  const [atBottom, setAtBottom] = useState(true);
  const [unseen, setUnseen] = useState(false);
  const atBottomRef = useRef(true);
  const lastAnswerRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(turns.length);
  const scrollToBottom = useCallback((smooth = true) => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    setUnseen(false);
  }, []);
  const onListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (bottom !== atBottomRef.current) { // état mis à jour seulement quand il change
      atBottomRef.current = bottom;
      setAtBottom(bottom);
      if (bottom) setUnseen(false);
    }
  };

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(turns.slice(-30))); } catch { /* stockage indisponible */ }
    const added = turns.length > prevCountRef.current;
    prevCountRef.current = turns.length;
    if (!added) return;
    const last = turns[turns.length - 1];
    requestAnimationFrame(() => {
      if (last?.role === 'user') { scrollToBottom(); return; }
      if (!atBottomRef.current) { setUnseen(true); return; }
      const el = listRef.current, ans = lastAnswerRef.current;
      if (el && ans) el.scrollTo({ top: Math.max(0, ans.offsetTop - 12), behavior: 'smooth' });
    });
  }, [turns, scrollToBottom]);

  useEffect(() => { if (busy && atBottomRef.current) requestAnimationFrame(() => scrollToBottom()); }, [busy, scrollToBottom]);

  // Ouverture : focus sur la saisie, Échap pour fermer, et coupure du « tirer pour
  // rafraîchir » du navigateur tant que la fenêtre est ouverte (sinon, en remontant la
  // conversation, un geste appuyé rechargeait la page). Restauré à la fermeture.
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      (tab === 'home' ? homeInputRef.current : inputRef.current)?.focus({ preventScroll: true });
      if (tab === 'chat') scrollToBottom(false);
    }, 150);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    const html = document.documentElement, body = document.body;
    const prev = { h: html.style.overscrollBehaviorY, b: body.style.overscrollBehaviorY };
    html.style.overscrollBehaviorY = 'none';
    body.style.overscrollBehaviorY = 'none';
    return () => {
      window.removeEventListener('keydown', onKey);
      html.style.overscrollBehaviorY = prev.h;
      body.style.overscrollBehaviorY = prev.b;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tab === 'chat') requestAnimationFrame(() => scrollToBottom(false)); }, [tab, scrollToBottom]);

  // Hauteur de la fenêtre calculée en JS à partir de la hauteur réelle de l'écran.
  // Pas de `dvh` ni de `min()` en CSS : les navigateurs de tablette anciens (Fully Kiosk,
  // WebView Android, Samsung Internet) ignorent la règle entière, la fenêtre prend alors la
  // hauteur de son contenu, le bas est rogné et plus rien ne défile.
  const [viewportH, setViewportH] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  useEffect(() => {
    const update = () => setViewportH(window.visualViewport?.height ?? window.innerHeight);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);
  const panelHeight = Math.max(320, Math.min(640, Math.round(viewportH) - 110));

  // Zones de saisie qui grandissent avec le texte
  useEffect(() => {
    for (const ta of [inputRef.current, homeInputRef.current]) {
      if (!ta) continue;
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }
  }, [input, tab]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    const next: Turn[] = [...turns.filter(t => !t.error), { role: 'user', content: q }];
    setTurns(next);
    setInput('');
    setTab('chat');
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await askAssistant(next.map(t => ({ role: t.role, content: t.content })), ctrl.signal);
      setTurns(t => [...t, { role: 'assistant', content: res.reply, attachments: res.attachments, toolsUsed: res.toolsUsed }]);
    } catch (e) {
      if (ctrl.signal.aborted) return;
      const msg = (e as Error).message || tr('adm_ai_err_unknown');
      setTurns(t => [...t, { role: 'assistant', content: tr('adm_ai_err_reply', { msg }), error: true }]);
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }, [busy, turns, tr]);

  // La fenêtre ne couvre pas la page : on peut suivre un lien sans la fermer.
  const onLink = useCallback((href: string) => { navigate(href); }, [navigate]);
  const reset = () => { abortRef.current?.abort(); setBusy(false); setTurns([]); setTab('home'); };

  // Rendu des messages mémorisé : taper ou faire défiler ne redessine pas toute la conversation.
  const renderedTurns = useMemo(() => turns.map((t, i) => t.role === 'user' ? (
    <div key={i} className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-zce-blue text-white px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap">{t.content}</div>
    </div>
  ) : (
    <div key={i} ref={i === turns.length - 1 ? lastAnswerRef : undefined} className="flex justify-start items-start gap-2">
      <AssistantAvatar size={26} />
      <div className={`min-w-0 flex-1 rounded-2xl rounded-tl-md border px-3.5 py-3 text-sm leading-relaxed ${t.error ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-white border-slate-200 text-slate-800'}`}>
        <Markdown text={t.content} onLink={onLink} />
        {t.attachments?.map((a, j) => <SeriesChart key={j} a={a} />)}
        {!!t.toolsUsed?.length && (
          <div className="mt-2.5 pt-2 border-t border-slate-100 text-[11px] text-slate-400">
            {tr('adm_ai_data_used')} {[...new Set(t.toolsUsed.map(n => (TOOL_LABELS[n] ? tr(TOOL_LABELS[n]) : n)))].join(', ')}
          </div>
        )}
      </div>
    </div>
  )), [turns, onLink, tr]);

  const firstName = user?.firstName || '';
  const lastQuestion = turns.filter(t => t.role === 'user').slice(-1)[0]?.content;
  const sendIcon = <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>;
  const keySend = (e: React.KeyboardEvent<HTMLTextAreaElement>) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } };

  return (
    <>
      {/* Bouton du bandeau */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`ml-2 inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 text-sm font-semibold transition-colors border ${open ? 'bg-zce-navy text-white border-zce-navy' : 'text-zce-navy border-blue-100 bg-gradient-to-r from-sky-50 to-emerald-50 hover:border-zce-blue'}`}
        aria-expanded={open}
        aria-controls="zos-assistant"
        title={tr('adm_ai_name')}
      >
        <AssistantAvatar size={28} />
        <span className="hidden 2xl:inline">{tr('adm_ai_button')}</span>
      </button>

      {/* Fenêtre flottante, en bas à droite : la page reste visible et utilisable derrière */}
      <section
        id="zos-assistant"
        aria-label={tr('adm_ai_name')}
        aria-hidden={!open}
        style={{ height: panelHeight }}
        className={`fixed z-[61] right-3 sm:right-6 bottom-[84px] w-[calc(100vw-24px)] sm:w-[400px] flex flex-col overflow-hidden rounded-2xl bg-[#f6f8fb] border border-slate-200 shadow-[0_18px_50px_rgba(13,59,102,.28)] origin-bottom-right transition-all duration-200 ${open ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-3 scale-[.98] pointer-events-none invisible'}`}
      >
        {/* En-tête */}
        <header className="flex-none bg-zce-navy text-white px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <AssistantAvatar size={30} />
            <div className="min-w-0">
              <div className="font-heading font-bold text-[15px] leading-tight">{tr('adm_ai_name')}</div>
              <div className="flex items-center gap-1.5 text-[11px] text-white/70">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 zce-pulse-dot" /> {tr('adm_ai_status')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-0.5 flex-none">
            {turns.length > 0 && (
              <button type="button" onClick={reset} className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10" title={tr('adm_ai_new_conversation')} aria-label={tr('adm_ai_new_conversation')}>
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
              </button>
            )}
            <button type="button" onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-white/10" aria-label={tr('adm_close')}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
        </header>

        {/* ── Onglet Accueil ── */}
        {tab === 'home' && (
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[13px] text-emerald-900">
              <div className="font-semibold">💡 {tr('adm_ai_readonly_title')}</div>
              <div className="text-emerald-800">{tr('adm_ai_readonly_text')}</div>
            </div>

            <div className="mt-6 flex flex-col items-center text-center">
              <AssistantAvatar size={56} glow />
              <h2 className="mt-3 font-heading text-[22px] font-extrabold text-slate-800 leading-snug [text-wrap:balance]">
                {firstName ? tr('adm_ai_hello_name', { name: firstName }) : tr('adm_ai_hello')} 👋<br />{tr('adm_ai_intro')}
              </h2>
              <p className="mt-3 text-[13px] text-slate-600 leading-relaxed max-w-[320px]">
                {tr('adm_ai_intro_text')}
              </p>
              <p className="mt-3 text-[12px] text-slate-500 leading-relaxed max-w-[320px]">
                ℹ️ {tr('adm_ai_privacy_note')}
              </p>
            </div>

            {turns.length > 0 && (
              <button type="button" onClick={() => setTab('chat')} className="mt-5 w-full flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-left hover:border-zce-blue transition-colors">
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-zce-navy">{tr('adm_ai_resume')}</span>
                  <span className="block text-[12px] text-slate-500 truncate">{lastQuestion}</span>
                </span>
                <svg className="w-4 h-4 text-slate-400 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><path d="M9 6l6 6-6 6" /></svg>
              </button>
            )}

            <form className="mt-5 relative" onSubmit={e => { e.preventDefault(); send(input); }}>
              <textarea
                ref={homeInputRef}
                value={input}
                onChange={e => setInput(e.target.value.slice(0, 2000))}
                onKeyDown={keySend}
                rows={1}
                placeholder={tr('adm_ai_placeholder')}
                className="w-full resize-none rounded-2xl border-2 border-slate-200 bg-white pl-4 pr-14 py-4 text-sm shadow-sm placeholder:italic placeholder:text-slate-400 focus:outline-none focus:border-zce-blue"
              />
              <button type="submit" disabled={busy || !input.trim()} aria-label={tr('adm_ai_send')}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-zce-navy text-white flex items-center justify-center disabled:bg-slate-100 disabled:text-slate-400 transition-colors">
                {sendIcon}
              </button>
            </form>

            <div className="mt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{tr('adm_ai_suggestions')}</div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTION_KEYS.map(k => tr(k)).map(s => (
                  <button key={s} type="button" onClick={() => send(s)} className="text-left text-[12.5px] rounded-full border border-slate-200 bg-white hover:border-zce-blue hover:text-zce-blue px-3 py-1.5 transition-colors">{s}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Onglet Conversation ── */}
        {tab === 'chat' && (
          <>
            <div className="relative flex-1 min-h-0 flex flex-col">
              <div ref={listRef} onScroll={onListScroll} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-4" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
                {turns.length === 0 && !busy && (
                  <div className="h-full flex flex-col items-center justify-center text-center text-sm text-slate-500 gap-3">
                    <AssistantAvatar size={44} />
                    {tr('adm_ai_no_conversation')}
                    <button type="button" onClick={() => setTab('home')} className="text-zce-blue font-semibold">{tr('adm_ai_ask')}</button>
                  </div>
                )}
                {renderedTurns}
                {busy && (
                  <div className="flex justify-start items-start gap-2">
                    <AssistantAvatar size={26} />
                    <div className="rounded-2xl rounded-tl-md border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-500 flex items-center gap-2">
                      <span className="inline-flex gap-1" aria-hidden="true">
                        <span className="w-1.5 h-1.5 rounded-full bg-zce-blue animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zce-blue animate-bounce [animation-delay:120ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zce-blue animate-bounce [animation-delay:240ms]" />
                      </span>
                      {tr('adm_ai_thinking')}
                    </div>
                  </div>
                )}
              </div>
              {(!atBottom || unseen) && turns.length > 0 && (
                <button type="button" onClick={() => scrollToBottom()}
                  className={`absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold shadow-lg border ${unseen ? 'bg-zce-blue text-white border-zce-blue' : 'bg-white text-zce-navy border-slate-200'}`}>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M6 13l6 6 6-6" /></svg>
                  {unseen ? tr('adm_ai_new_reply') : tr('adm_ai_scroll_bottom')}
                </button>
              )}
            </div>
            <form className="flex-none px-3 pt-2 pb-2 bg-[#f6f8fb]" onSubmit={e => { e.preventDefault(); send(input); }}>
              <div className="relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value.slice(0, 2000))}
                  onKeyDown={keySend}
                  rows={1}
                  placeholder={tr('adm_ai_placeholder')}
                  disabled={busy}
                  className="block w-full resize-none rounded-2xl border-2 border-slate-200 bg-white pl-4 pr-14 py-3 text-sm placeholder:italic placeholder:text-slate-400 focus:outline-none focus:border-zce-blue disabled:opacity-60"
                />
                <button type="submit" disabled={busy || !input.trim()} aria-label={tr('adm_ai_send')}
                  className="absolute right-2.5 bottom-2 h-8 w-8 rounded-full bg-zce-navy text-white flex items-center justify-center disabled:bg-slate-100 disabled:text-slate-400 transition-colors">
                  {sendIcon}
                </button>
              </div>
            </form>
          </>
        )}

        {/* ── Onglet Aide ── */}
        {tab === 'help' && (
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-3 text-[13px] text-slate-700 leading-relaxed" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
            {getHelpSections(tr).map(sec => (
              <div key={sec.t} className="rounded-xl bg-white border border-slate-200 p-3.5">
                <div className="font-semibold text-zce-navy mb-1.5">{sec.t}</div>
                <ul className="list-disc pl-4 space-y-1">{sec.b.map(x => <li key={x}>{x}</li>)}</ul>
              </div>
            ))}
          </div>
        )}

        {/* Barre d'onglets */}
        <nav className="flex-none grid grid-cols-3 gap-1 p-2 bg-white border-t border-slate-200" aria-label={tr('adm_ai_sections')}>
          {([['home', tr('adm_ai_tab_home')], ['chat', tr('adm_ai_tab_chat')], ['help', tr('adm_ai_tab_help')]] as Array<[Tab, string]>).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} aria-current={tab === k ? 'page' : undefined}
              className={`relative flex flex-col items-center gap-1 rounded-xl py-2 text-[12px] font-medium transition-colors ${tab === k ? 'bg-slate-100 text-zce-blue' : 'text-slate-600 hover:bg-slate-50'}`}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">{TabIcon[k]}</svg>
              {label}
              {k === 'chat' && unseen && tab !== 'chat' && <span className="absolute top-1.5 right-[30%] w-2 h-2 rounded-full bg-zce-orange" />}
            </button>
          ))}
        </nav>
      </section>

      {/* Bouton rond de réduction, sous la fenêtre (comme les widgets de support) */}
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label={tr('adm_ai_minimise')}
        className={`fixed z-[62] right-3 sm:right-6 bottom-4 h-14 w-14 rounded-full bg-zce-navy text-white shadow-xl flex items-center justify-center transition-all duration-200 hover:bg-zce-blue ${open ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none invisible'}`}
      >
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
    </>
  );
}

export default AssistantPanel;
